/**
 * A Victron off-grid system, as its owner sees it (Postman's Crossing brief §8). Mobile-first: one
 * column of cards — battery, solar and house load, the generator with its three modes, and the
 * owner's own setpoints. Everything interpretive (is the generator running, what mode is it in, which
 * tiles exist) is decided by the API; this page draws it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../auth';
import { ApiError, type GenMode, type GenState, type VictronView } from '../api';
import { Banner, Button } from '../ui';

const POLL_MS = 4000;
const CMD_POLL_MS = 1000;
const CMD_POLL_TRIES = 25;

type CmdPhase =
  | { kind: 'idle' }
  | { kind: 'sending'; what: string }
  | { kind: 'done'; ok: boolean; text: string };

const MODE_LABEL: Record<GenMode, string> = { stop: 'Stop', auto: 'Auto', start: 'Start' };

const STATE_PILL: Record<GenState, { cls: string; text: string }> = {
  running: { cls: 'good', text: 'Running' },
  starting: { cls: 'warn', text: 'Starting' },
  standby: { cls: 'idle', text: 'Standby' },
  stopped: { cls: 'idle', text: 'Stopped' },
  failed: { cls: 'crit', text: 'Failed to start' },
  unknown: { cls: 'idle', text: '—' },
};

function kw(w: number | null): string {
  if (w === null) return '—';
  return `${(w / 1000).toFixed(1)} kW`;
}

function ago(iso: string | null, now: number): string {
  if (!iso) return 'no data yet';
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${Math.round(s / 3600)} h`;
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase();
}

/** SOC ring. Coloured against the owner's own low-battery threshold, not a fixed number. */
function SocRing({ soc, lowPct }: { soc: number | null; lowPct: number }): JSX.Element {
  const r = 44;
  const c = 2 * Math.PI * r;
  const pct = soc === null ? 0 : Math.min(100, Math.max(0, soc));
  const colour =
    soc === null
      ? 'var(--faint)'
      : soc < lowPct
        ? 'var(--crit)'
        : soc < lowPct + 15
          ? 'var(--amber)'
          : 'var(--good)';
  return (
    <svg className="vx-ring" viewBox="0 0 100 100" role="img" aria-label={`Battery ${soc ?? '—'}%`}>
      <circle cx="50" cy="50" r={r} fill="none" stroke="var(--line)" strokeWidth="8" />
      <circle
        cx="50"
        cy="50"
        r={r}
        fill="none"
        stroke={colour}
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={`${(pct / 100) * c} ${c}`}
        transform="rotate(-90 50 50)"
      />
      <text x="50" y="50" textAnchor="middle" dominantBaseline="central" className="vx-ring-text">
        {soc === null ? '—' : `${Math.round(soc)}%`}
      </text>
    </svg>
  );
}

function batteryLine(p: number | null): string {
  if (p === null) return '—';
  if (Math.abs(p) < 50) return 'Idle';
  return p > 0 ? `Charging +${kw(p)}` : `Discharging ${kw(p).replace('-', '−')}`;
}

export function VictronSystem({ controllerId }: { controllerId?: string }): JSX.Element {
  const params = useParams<{ id: string }>();
  const id = controllerId ?? params.id!;
  const { api } = useAuth();
  const [view, setView] = useState<VictronView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [ask, setAsk] = useState<GenMode | null>(null);
  const [cmd, setCmd] = useState<CmdPhase>({ kind: 'idle' });
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const v = await api.getVictronSystem(id);
      if (!alive.current) return;
      setView(v);
      setErr(null);
    } catch (e) {
      if (alive.current) setErr(e instanceof ApiError ? e.message : 'Could not reach SentIQ.');
    }
  }, [api, id]);

  useEffect(() => {
    alive.current = true;
    void load();
    const t = setInterval(() => void load(), POLL_MS);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      alive.current = false;
      clearInterval(t);
      clearInterval(tick);
    };
  }, [load]);

  /** Follow a command until the Cerbo confirms it (or the server gives up on it). */
  const track = useCallback(
    async (what: string, issue: () => Promise<{ commandId: string }>) => {
      setCmd({ kind: 'sending', what });
      try {
        const { commandId } = await issue();
        for (let i = 0; i < CMD_POLL_TRIES; i++) {
          await new Promise((r) => setTimeout(r, CMD_POLL_MS));
          const r = await api.getCommand(commandId);
          if (r.status === 'confirmed') {
            setCmd({ kind: 'done', ok: true, text: `${what} confirmed by the Cerbo.` });
            void load();
            return;
          }
          if (r.status === 'failed' || r.status === 'rejected') {
            setCmd({
              kind: 'done',
              ok: false,
              text: `${what} was not confirmed. ${r.error ?? ''}`,
            });
            void load();
            return;
          }
        }
        setCmd({
          kind: 'done',
          ok: false,
          text: `${what}: no confirmation yet — is the site online?`,
        });
      } catch (e) {
        setCmd({
          kind: 'done',
          ok: false,
          text: e instanceof ApiError ? e.message : `${what} failed.`,
        });
      }
    },
    [api, load],
  );

  const setMode = (mode: GenMode) =>
    void track(`${MODE_LABEL[mode]}`, () => api.setGeneratorMode(id, mode));

  if (!view) {
    return (
      <div className="vx">
        {err ? <Banner kind="err">{err}</Banner> : <p className="muted">Loading…</p>}
      </div>
    );
  }

  const g = view.generator;
  const pill = STATE_PILL[g.state];
  const busy = cmd.kind === 'sending';
  const showSolar = view.capabilities.includes('pv.ac') || view.capabilities.includes('pv.dc');
  const solarSub = [
    view.capabilities.includes('pv.ac') ? 'AC-coupled' : null,
    view.capabilities.includes('pv.dc') ? 'MPPT' : null,
  ]
    .filter(Boolean)
    .join(' + ');
  // Says what the current mode will do next, in the owner's terms.
  const autoLine =
    g.mode === 'start'
      ? 'runs until you press Stop or Auto'
      : g.mode === 'stop'
        ? 'auto start is off'
        : g.socAutostartEnabled && g.socStart !== null && g.socStop !== null
          ? `auto starts below ${g.socStart}% · stops at ${g.socStop}%`
          : 'auto start follows the Cerbo’s own rules';
  const lc = view.lastCommand;

  return (
    <div className="vx">
      <div className="vx-head">
        <div>
          <h1 className="vx-title">{view.siteName}</h1>
          <div className="muted">{view.systemName}</div>
        </div>
        <span className={`pill ${view.online ? 'good' : 'crit'}`}>
          <span className="dot" />
          {view.online ? 'Online' : 'Offline'} · {ago(view.lastUpdate, now)}
        </span>
      </div>

      {err && <Banner kind="warn">{err} Showing the last reading.</Banner>}
      {view.alarms.map((a) => (
        <Banner key={a.code} kind={a.severity === 'warning' ? 'warn' : 'err'}>
          {a.label}
        </Banner>
      ))}

      <section className="panel vx-card vx-battery">
        <SocRing soc={view.battery.soc} lowPct={view.settings.lowSocAlarmPct} />
        <div>
          <div className="muted">Battery</div>
          <div className="vx-big">{batteryLine(view.battery.power)}</div>
          <div className="muted">
            {view.battery.voltage !== null ? `${view.battery.voltage.toFixed(1)} V` : '—'}
            {view.battery.description ? ` · ${view.battery.description}` : ''}
          </div>
        </div>
      </section>

      <div className="vx-tiles">
        {showSolar && (
          <section className="panel vx-card">
            <div className="muted">☀ Solar</div>
            <div className="vx-big">{kw(view.solar.power)}</div>
            <div className="muted">{solarSub}</div>
          </section>
        )}
        <section className="panel vx-card">
          <div className="muted">⌂ House load</div>
          <div className="vx-big">{kw(view.load.power)}</div>
          <div className="muted">
            {view.load.todayAvg !== null ? `Today avg ${kw(view.load.todayAvg)}` : ' '}
          </div>
        </section>
      </div>

      <section className="panel vx-card">
        <div className="vx-gen-head">
          <h2 className="vx-h2">Generator</h2>
          <span className={`pill ${pill.cls}`}>{pill.text}</span>
        </div>
        <div className="muted vx-gen-line">
          {kw(g.power)} · {g.runReason && g.state !== 'stopped' ? `${g.runReason} · ` : ''}
          {autoLine}
        </div>

        {view.capabilities.includes('gen.control') && (
          <div className="vx-modes" role="group" aria-label="Generator mode">
            {(['stop', 'auto', 'start'] as GenMode[]).map((m) => (
              <button
                key={m}
                className={`btn ${g.mode === m ? 'btn-primary' : 'btn-ghost'} vx-mode`}
                aria-pressed={g.mode === m}
                disabled={!view.canControl || busy || !view.online}
                onClick={() => (m === 'auto' ? setMode(m) : setAsk(m))}
              >
                {MODE_LABEL[m]}
              </button>
            ))}
          </div>
        )}
        {!view.online && view.canControl && (
          <div className="muted vx-note">
            The site is offline — the Cerbo keeps running its own settings.
          </div>
        )}
        {cmd.kind === 'sending' && (
          <div className="muted vx-note">Sending {cmd.what}… waiting for the Cerbo.</div>
        )}
        {cmd.kind === 'done' && <Banner kind={cmd.ok ? 'ok' : 'err'}>{cmd.text}</Banner>}

        <div className="vx-gen-foot">
          <span>{g.runHours !== null ? `Run hours ${g.runHours.toLocaleString()} h` : ''}</span>
          {lc && (
            <span className="muted">
              {lc.label} · {lc.by ?? '—'} · {clock(lc.at)}{' '}
              {lc.status === 'confirmed' ? '✓' : lc.status === 'pending' ? '…' : '✕'}
            </span>
          )}
        </div>
      </section>

      {view.canControl && (
        <Setpoints view={view} busy={busy} track={track} onSaved={() => void load()} />
      )}

      {ask && (
        <div className="modal-overlay" onClick={() => setAsk(null)}>
          <div className="panel modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="card-accent" />
            <div className="panel-pad">
              <p style={{ marginTop: 0 }}>
                {ask === 'start'
                  ? 'Start the generator now? It keeps running until you press Stop or Auto.'
                  : 'Stop the generator and switch auto start off? It will not start on low battery until you press Auto.'}
              </p>
              <div className="form-actions">
                <button className="btn btn-ghost" onClick={() => setAsk(null)}>
                  Cancel
                </button>
                <Button
                  onClick={() => {
                    const m = ask;
                    setAsk(null);
                    setMode(m);
                  }}
                >
                  Yes, {MODE_LABEL[ask].toLowerCase()}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The owner's adjustable points. Generator start/stop are written to the Cerbo (so autostart keeps
 * working when the link is down); the low-battery alarm is SentIQ's own threshold.
 */
function Setpoints({
  view,
  busy,
  track,
  onSaved,
}: {
  view: VictronView;
  busy: boolean;
  track: (what: string, issue: () => Promise<{ commandId: string }>) => Promise<void>;
  onSaved: () => void;
}): JSX.Element {
  const { api } = useAuth();
  const g = view.generator;
  const [start, setStart] = useState(g.socStart ?? 30);
  const [stop, setStop] = useState(g.socStop ?? 80);
  const [low, setLow] = useState(view.settings.lowSocAlarmPct);
  const [lowMsg, setLowMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [savingLow, setSavingLow] = useState(false);

  // Follow the site when the owner isn't mid-edit (someone else, or the Cerbo, changed it).
  const dirtyGen = start !== (g.socStart ?? 30) || stop !== (g.socStop ?? 80);
  useEffect(() => {
    if (!dirtyGen) {
      setStart(g.socStart ?? 30);
      setStop(g.socStop ?? 80);
    }
  }, [g.socStart, g.socStop]);

  const bandOk = stop >= start + 5;
  const showGen = view.capabilities.includes('gen.soc_autostart');

  return (
    <section className="panel vx-card">
      <h2 className="vx-h2">Settings</h2>

      {showGen && (
        <div className="vx-setting">
          <div className="vx-setting-head">
            <span>Generator auto start</span>
            {g.socAutostartEnabled === false && (
              <span className="pill warn">SOC start off on the Cerbo</span>
            )}
          </div>
          <label className="vx-slider">
            <span>Start below</span>
            <input
              type="range"
              min={5}
              max={90}
              value={start}
              onChange={(e) => setStart(Number(e.target.value))}
            />
            <b>{start}%</b>
          </label>
          <label className="vx-slider">
            <span>Stop at</span>
            <input
              type="range"
              min={10}
              max={100}
              value={stop}
              onChange={(e) => setStop(Number(e.target.value))}
            />
            <b>{stop}%</b>
          </label>
          {!bandOk && <div className="muted vx-note">Stop must be at least 5% above start.</div>}
          <Button
            className="btn btn-primary btn-sm"
            disabled={!dirtyGen || !bandOk || busy || !view.online}
            onClick={() =>
              void track('Auto start levels', () =>
                api.setGeneratorSoc(view.controllerId, start, stop),
              )
            }
          >
            Save to Cerbo
          </Button>
        </div>
      )}

      <div className="vx-setting">
        <div className="vx-setting-head">
          <span>Low battery alarm</span>
        </div>
        <label className="vx-slider">
          <span>Alert below</span>
          <input
            type="range"
            min={5}
            max={95}
            value={low}
            onChange={(e) => setLow(Number(e.target.value))}
          />
          <b>{low}%</b>
        </label>
        {lowMsg && <Banner kind={lowMsg.ok ? 'ok' : 'err'}>{lowMsg.text}</Banner>}
        <Button
          className="btn btn-primary btn-sm"
          busy={savingLow}
          disabled={low === view.settings.lowSocAlarmPct}
          onClick={() => {
            setSavingLow(true);
            api
              .setLowSocAlarm(view.controllerId, low)
              .then(() => {
                setLowMsg({ ok: true, text: `You'll be alerted below ${low}%.` });
                onSaved();
              })
              .catch((e: unknown) =>
                setLowMsg({
                  ok: false,
                  text: e instanceof ApiError ? e.message : 'Could not save.',
                }),
              )
              .finally(() => setSavingLow(false));
          }}
        >
          Save alarm
        </Button>
      </div>

      <SolarArray view={view} onSaved={onSaved} />
      <ReportPreview controllerId={view.controllerId} />
    </section>
  );
}

/**
 * Installed PV size — it scales the forecast in the 07:00 report. Deliberately guarded: it should
 * only change when the array does, on the installer's word, and the page makes the owner say so.
 */
function SolarArray({ view, onSaved }: { view: VictronView; onSaved: () => void }): JSX.Element {
  const { api } = useAuth();
  const current = view.settings.pvArrayKwp;
  const [open, setOpen] = useState(false);
  const [kwp, setKwp] = useState(current !== null ? String(current) : '');
  const [directed, setDirected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const n = Number(kwp);
  const valid = kwp.trim() !== '' && Number.isFinite(n) && n >= 0.5 && n <= 100;

  return (
    <div className="vx-setting">
      <div className="vx-setting-head">
        <span>Solar array size</span>
      </div>
      <div className="muted">
        {current !== null ? `${current} kWp` : 'Not set — the daily report shows sun-hours only.'}
        {view.settings.pvArrayUpdatedAt
          ? ` · changed ${new Date(view.settings.pvArrayUpdatedAt).toLocaleDateString()}`
          : ''}
      </div>
      {msg && <Banner kind={msg.ok ? 'ok' : 'err'}>{msg.text}</Banner>}
      {!open ? (
        <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
          Change array size
        </button>
      ) : (
        <>
          <Banner kind="warn">
            ⚠ Only change this under direction of your site installer. It sets the solar forecast in
            your daily report and should match the panels actually installed.
          </Banner>
          <label className="vx-slider">
            <span>Array (kWp)</span>
            <input
              className="input mono"
              type="number"
              inputMode="decimal"
              min={0.5}
              max={100}
              step={0.1}
              value={kwp}
              onChange={(e) => setKwp(e.target.value)}
            />
            <span />
          </label>
          <label className="chk">
            <input
              type="checkbox"
              checked={directed}
              onChange={(e) => setDirected(e.target.checked)}
            />
            My site installer has directed this change
          </label>
          <div className="form-actions">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setOpen(false);
                setDirected(false);
                setKwp(current !== null ? String(current) : '');
              }}
            >
              Cancel
            </button>
            <Button
              className="btn btn-primary btn-sm"
              busy={busy}
              disabled={!valid || !directed}
              onClick={() => {
                setBusy(true);
                api
                  .setSolarArray(view.controllerId, n, directed)
                  .then(() => {
                    setMsg({ ok: true, text: `Array size set to ${n} kWp.` });
                    setOpen(false);
                    setDirected(false);
                    onSaved();
                  })
                  .catch((e: unknown) =>
                    setMsg({
                      ok: false,
                      text: e instanceof ApiError ? e.message : 'Could not save.',
                    }),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              Save array size
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/** What the 07:00 Telegram report would say right now. */
function ReportPreview({ controllerId }: { controllerId: string }): JSX.Element {
  const { api } = useAuth();
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="vx-setting">
      <div className="vx-setting-head">
        <span>Daily report</span>
      </div>
      <div className="muted">Sent to Telegram at 7:00 am each morning.</div>
      {err && <Banner kind="err">{err}</Banner>}
      {text && <pre className="vx-report">{text}</pre>}
      <Button
        className="btn btn-ghost btn-sm"
        busy={busy}
        onClick={() => {
          setBusy(true);
          setErr(null);
          api
            .getReportPreview(controllerId)
            .then((r) => setText(r.text))
            .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Could not load.'))
            .finally(() => setBusy(false));
        }}
      >
        {text ? 'Refresh preview' : "Preview this morning's report"}
      </Button>
    </div>
  );
}

/** Landing for an owner: their one system, or a short list if they somehow have several. */
export function VictronHome(): JSX.Element {
  const { api } = useAuth();
  const [systems, setSystems] = useState<{ controllerId: string; siteName: string }[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    api
      .listVictronSystems()
      .then((r) => setSystems(r.systems))
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Could not reach SentIQ.'));
  }, [api]);
  const [picked, setPicked] = useState<string | null>(null);

  if (err)
    return (
      <div className="vx">
        <Banner kind="err">{err}</Banner>
      </div>
    );
  if (!systems)
    return (
      <div className="vx">
        <p className="muted">Loading…</p>
      </div>
    );
  if (systems.length === 0)
    return (
      <div className="vx">
        <p className="muted">No system is connected to this account yet.</p>
      </div>
    );
  const chosen = picked ?? (systems.length === 1 ? systems[0]!.controllerId : null);
  if (chosen) return <VictronSystem controllerId={chosen} />;
  return (
    <div className="vx">
      {systems.map((s) => (
        <button
          key={s.controllerId}
          className="panel vx-card vx-pick"
          onClick={() => setPicked(s.controllerId)}
        >
          {s.siteName}
        </button>
      ))}
    </div>
  );
}
